import { Input } from '@/components/atoms/Form/Input';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import Select, { Option } from '@/components/atoms/Select';
import { Text } from '@/components/atoms/Text';
import { SavingBar } from '@/components/atoms/SavingBar';
import Modal from '@/components/atoms/Modal';
import { SvyButton } from '@/components/savory/SvyButton';
import { computeCurrency, computeText } from '@/locales/utils';
import { computeAndTriggerTouchedFields } from '@/utils/form/utils';
import {
    BaseEntranceFeeAttributes,
    EntranceFeeType,
    EntranceFeePayload,
    EntranceFeeStrategy,
    VatEnum,
    EntranceFee,
} from '@/services/innovorder/entranceFee/entranceFee.type';
import { getEntranceFee, getEntranceFeeRestaurants, updateEntranceFee } from '@/redux/grantAndEntranceFee';
import { useShift } from '@/hooks/useShift/useShift';
import { EntranceFeeRestaurants } from './EntranceFeeRestaurants';
import {
    ParentContainer,
    Container,
    FlexContainer,
    TitleContainer,
    CustomTitle,
    FormContainer,
    MargedTitle,
    Flex,
    FormChildrens,
    WarningFormContainer,
    EntranceFeeRestaurantsContainer,
} from './EntranceFeeForm.style';
import EntranceFeeShifts from './EntranceFeeShifts/EntranceFeeShifts';

export const EntranceFeeStrategyTranslationMap = {
    [EntranceFeeStrategy.Always]: 'entranceFee.select.always',
    [EntranceFeeStrategy.Daily_minimum_granted]: 'entranceFee.select.daily',
};

export const entranceFeeStrategyMap: Option<EntranceFeeStrategy>[] = [
    { label: EntranceFeeStrategyTranslationMap[EntranceFeeStrategy.Always], value: EntranceFeeStrategy.Always },
    {
        label: EntranceFeeStrategyTranslationMap[EntranceFeeStrategy.Daily_minimum_granted],
        value: EntranceFeeStrategy.Daily_minimum_granted,
    },
];

export const entranceFeeCAMap: Option<EntranceFeeType>[] = [
    { label: 'entranceFee.select.salesExcluded', value: EntranceFeeType.RoyaltyFee },
    { label: 'entranceFee.select.salesIncluded', value: EntranceFeeType.AdmissionFee },
];

export const vatMap: Option<VatEnum>[] = [
    { label: '0 %', value: VatEnum.zero },
    { label: '2.1 %', value: VatEnum.twoOne },
    { label: '5.5 %', value: VatEnum.fiveFive },
    { label: '6 %', value: VatEnum.six },
    { label: '8.5 %', value: VatEnum.heigthFive },
    { label: '10 %', value: VatEnum.ten },
    { label: '20 %', value: VatEnum.twenty },
    { label: '21 %', value: VatEnum.twentyOne },
];

type EntranceFeeFormProps = {
    entranceFee?: EntranceFeePayload;
    entranceFees?: EntranceFee[] | undefined;
    error?: string;
    loading: boolean;
    updateSuccess?: boolean;
    onSubmit: () => void;
    isCreateMode?: boolean;
};

type DangerousItem = {
    key: string;
    name: string;
    amount: number;
    diff: number;
};

const EntranceFeeForm: React.FunctionComponent<React.PropsWithChildren<EntranceFeeFormProps>> = ({
    entranceFee,
    entranceFees,
    error: callError,
    loading,
    isCreateMode,
    onSubmit,
}) => {
    const intl = useIntl();
    const [modal, setModal] = useState<{ restaurantId: number; name: string } | null>(null);
    const [existingEntranceFeesCodes, setExistingEntranceFeesCodes] = useState<string[] | undefined>();

    const dispatch = useDispatch();
    const { hasShiftRequirement } = useShift();
    const entranceFeeFromState = useSelector(getEntranceFee);
    const entranceFeeRestaurantsFromState = useSelector(getEntranceFeeRestaurants);

    const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
    const [dangerousItems, setDangerousItems] = useState<DangerousItem[]>([]);
    const [canConfirm, setCanConfirm] = useState(false);

    const {
        control,
        formState: { touchedFields, dirtyFields, isSubmitSuccessful, isValid },
        handleSubmit,
        watch,
        trigger,
        reset,
        getValues,
    } = useForm<BaseEntranceFeeAttributes>({
        defaultValues: {
            strategy: EntranceFeeStrategy.Always,
            vat: VatEnum.zero,
            type: EntranceFeeType.AdmissionFee,
            restaurants: [],
            ...entranceFee,
        },
    });

    const handleOnConfigure = ({ restaurantId, name }: { restaurantId: number; name: string }) => {
        isValid && setModal({ restaurantId, name });
    };

    const handleOnHide = () => {
        setModal(null);
    };

    const finalTouchedFields = computeAndTriggerTouchedFields<EntranceFeePayload, BaseEntranceFeeAttributes>(
        entranceFee,
        touchedFields,
        trigger,
    );

    const handleCancelChanges = (): void => {
        reset(
            isCreateMode
                ? {
                      strategy: EntranceFeeStrategy.Always,
                      vat: VatEnum.zero,
                      type: EntranceFeeType.AdmissionFee,
                      amount: 0,
                      code: '',
                      dailyLimit: 0,
                      labelTicket: '',
                  }
                : {
                      strategy: EntranceFeeStrategy.Always,
                      vat: VatEnum.zero,
                      type: EntranceFeeType.AdmissionFee,
                      ...entranceFee,
                  },
        );
    };

    const handleFormSubmit = (data: BaseEntranceFeeAttributes) => {
        const globalAmountEuro = data.amount;
        const globalAmountCents = globalAmountEuro * 100;

        const restaurantsCustom = entranceFeeRestaurantsFromState;
        const allRestaurantsPayload = entranceFeeFromState.restaurants;

        const newDangerousItems: DangerousItem[] = [];

        // 1. Check custom restaurants (amounts are in Euros)
        restaurantsCustom.forEach((r) => {
            if (r.status === 'customised' && r.amount !== undefined) {
                const amountEuro = r.amount;
                if (amountEuro !== globalAmountEuro) {
                    newDangerousItems.push({
                        key: `resto-${r.restaurantId}`,
                        name: r.name,
                        amount: amountEuro * 100,
                        diff: Math.abs(amountEuro - globalAmountEuro) * 100,
                    });
                }
            }
        });

        // 2. Check shifts (amounts are in Cents)
        allRestaurantsPayload.forEach((rPayload) => {
            if (rPayload.shiftEntranceFees) {
                rPayload.shiftEntranceFees.forEach((shiftFee) => {
                    if (shiftFee.amount !== globalAmountCents) {
                        const shiftName = shiftFee.shift?.name || shiftFee.shiftId;
                        const restoName =
                            restaurantsCustom.find((rc) => rc.restaurantId === rPayload.restaurantId)?.name ||
                            `Restaurant ${rPayload.restaurantId}`;

                        newDangerousItems.push({
                            key: `shift-${rPayload.restaurantId}-${shiftFee.shiftId}`,
                            name: `${restoName} - ${shiftName}`,
                            amount: shiftFee.amount,
                            diff: Math.abs(shiftFee.amount - globalAmountCents),
                        });
                    }
                });
            }
        });

        if (newDangerousItems.length > 0) {
            setDangerousItems(newDangerousItems);
            setIsWarningModalOpen(true);
            return;
        }

        onSubmit();
    };

    const handleConfirmWarning = () => {
        setIsWarningModalOpen(false);
        onSubmit();
    };

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | undefined;

        if (isWarningModalOpen) {
            setCanConfirm(false);
            timer = setTimeout(() => {
                setCanConfirm(true);
            }, 2500);
        }

        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [isWarningModalOpen]);

    useEffect(() => {
        const subscription = watch((value) => {
            const updatedValue = hasShiftRequirement
                ? { ...value, restaurants: entranceFeeFromState.restaurants }
                : { ...value };
            dispatch(updateEntranceFee(updatedValue));
        });

        return () => subscription.unsubscribe();
    }, [watch, dispatch, entranceFeeFromState, hasShiftRequirement]);

    useEffect(() => {
        if (isSubmitSuccessful) {
            reset(getValues());
        }
    }, [isSubmitSuccessful, getValues, reset]);

    useEffect(() => {
        if (entranceFees && entranceFees.length) {
            setExistingEntranceFeesCodes(entranceFees.map((e) => e.code));
            return;
        }
        setExistingEntranceFeesCodes(undefined);
    }, [entranceFees]);

    return (
        <ParentContainer>
            <Modal
                isOpen={isWarningModalOpen}
                onHide={() => setIsWarningModalOpen(false)}
                title={computeText(intl, 'entranceFee.warningShifts.title')}
                buttons={[
                    <SvyButton
                        key="confirm-warning"
                        onClick={handleConfirmWarning}
                        disabled={!canConfirm}
                        color="primary"
                        variant="default"
                    >
                        {computeText(intl, 'button.confirm')}
                    </SvyButton>,
                ]}
            >
                <div>
                    <span>
                        {dangerousItems
                            .map((item) =>
                                computeText(intl, 'entranceFee.warningShifts.shift', {
                                    time: item.name,
                                    amount: computeCurrency({ intl, amount: item.amount }),
                                    variation: computeCurrency({
                                        intl,
                                        amount: item.diff,
                                    }),
                                }),
                            )
                            .join('\n')}
                    </span>
                    <br />
                    <span>{computeText(intl, 'entranceFee.warningShifts.confirm')}</span>
                </div>
            </Modal>
            <Container>
                <form>
                    <FlexContainer>
                        <div style={{ flex: 1, paddingRight: 50 }}>
                            <TitleContainer>
                                <CustomTitle text={entranceFee ? 'entranceFee.title' : 'entranceFee.create'} />
                                {entranceFee && <CustomTitle text={entranceFee.code} />}
                            </TitleContainer>
                            <Text text="entranceFee.description" />
                        </div>
                        <FormContainer data-testid="entranceFee-form">
                            <MargedTitle text="entranceFee.default" type="small" weight="medium" />
                            <Flex>
                                <FormChildrens style={{ marginRight: 20 }}>
                                    <Controller
                                        control={control}
                                        name="code"
                                        rules={{
                                            required: computeText(intl, 'field.required'),
                                            validate: (value) =>
                                                !existingEntranceFeesCodes ||
                                                !existingEntranceFeesCodes.includes(value) ||
                                                computeText(intl, 'entranceFee.codeAlreadyUsed'),
                                        }}
                                        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                                            <Input
                                                error={error?.message}
                                                onChange={onChange}
                                                onBlur={onBlur}
                                                value={value}
                                                placeholder="entranceFee.code"
                                                labelId="entranceFee.code"
                                                isTouched={finalTouchedFields.code}
                                                preventSpecialCharacters={true}
                                            />
                                        )}
                                    />
                                    <Controller
                                        control={control}
                                        name="amount"
                                        rules={{
                                            required: computeText(intl, 'field.required'),
                                        }}
                                        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                                            <Input
                                                error={
                                                    error?.message ||
                                                    (error?.type && computeText(intl, 'field.greaterThanZero'))
                                                }
                                                onChange={onChange}
                                                onBlur={onBlur}
                                                value={value?.toString()}
                                                placeholder="entranceFee.amount"
                                                type="number"
                                                unitType="currency"
                                                labelId="entranceFee.amount"
                                                isTouched={finalTouchedFields.amount}
                                                min={0}
                                                max={999999999}
                                            />
                                        )}
                                    />
                                    <Controller
                                        control={control}
                                        name="strategy"
                                        rules={{ required: computeText(intl, 'field.required') }}
                                        render={({ field: { onChange, onBlur, value } }) => (
                                            <Select<EntranceFeeStrategy>
                                                value={value}
                                                options={entranceFeeStrategyMap}
                                                onChange={onChange}
                                                onBlur={onBlur}
                                                labelId="entranceFee.strategy"
                                                isTouched={finalTouchedFields.strategy}
                                            />
                                        )}
                                    />
                                    <Controller
                                        control={control}
                                        name="type"
                                        render={({ field: { onChange, onBlur, value } }) => (
                                            <Select<EntranceFeeType>
                                                value={value ?? EntranceFeeType.AdmissionFee}
                                                options={entranceFeeCAMap}
                                                onChange={onChange}
                                                onBlur={onBlur}
                                                labelId="entranceFee.type"
                                                isTouched={value ? finalTouchedFields.type : undefined}
                                            />
                                        )}
                                    />
                                </FormChildrens>
                                <FormChildrens>
                                    <Controller
                                        control={control}
                                        name="labelTicket"
                                        rules={{ required: computeText(intl, 'field.required') }}
                                        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                                            <Input
                                                error={error?.message}
                                                onChange={onChange}
                                                onBlur={onBlur}
                                                value={value}
                                                placeholder="entranceFee.labelTicket"
                                                labelId="entranceFee.labelTicket"
                                                isTouched={finalTouchedFields.labelTicket}
                                            />
                                        )}
                                    />
                                    <Controller
                                        control={control}
                                        name="dailyLimit"
                                        rules={{ required: computeText(intl, 'field.required'), min: '1' }}
                                        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                                            <Input
                                                error={
                                                    error?.message ||
                                                    (error?.type && computeText(intl, 'field.greaterThanZero'))
                                                }
                                                onChange={onChange}
                                                onBlur={onBlur}
                                                value={value?.toString()}
                                                placeholder="entranceFee.dailyLimit"
                                                type="number"
                                                labelId="entranceFee.dailyLimit"
                                                isTouched={finalTouchedFields.dailyLimit}
                                                min={0}
                                                max={999999999}
                                            />
                                        )}
                                    />
                                    <Controller
                                        control={control}
                                        name="vat"
                                        rules={{ required: computeText(intl, 'field.required') }}
                                        render={({ field: { onChange, onBlur, value } }) => (
                                            <Select<VatEnum>
                                                value={value}
                                                options={vatMap}
                                                onChange={onChange}
                                                onBlur={onBlur}
                                                labelId="entranceFee.vat"
                                                isTouched={finalTouchedFields.vat !== undefined}
                                            />
                                        )}
                                    />
                                </FormChildrens>
                            </Flex>
                        </FormContainer>
                    </FlexContainer>

                    {modal && (
                        <EntranceFeeShifts
                            restaurantId={modal.restaurantId}
                            name={modal.name}
                            handleOnHide={handleOnHide}
                            isCreateMode={!!isCreateMode}
                        />
                    )}
                </form>

                <CustomTitle text="entranceFee.customUse" />
                <Text text="entranceFee.descCustomUse" />
                {hasShiftRequirement && !isValid && (
                    <WarningFormContainer>
                        <Text text="entranceFee.warningForm" />
                    </WarningFormContainer>
                )}
                <div style={{ marginTop: 20 }}>
                    <EntranceFeeRestaurantsContainer hasShiftRequirement={hasShiftRequirement} isValid={isValid}>
                        <EntranceFeeRestaurants isCreateMode={isCreateMode} handleOnConfigure={handleOnConfigure} />
                    </EntranceFeeRestaurantsContainer>
                </div>
            </Container>
            <SavingBar
                onSave={handleSubmit(handleFormSubmit)}
                error={callError}
                loading={loading}
                disabled={loading}
                changesCount={Object.keys(dirtyFields).length}
                onCancel={handleCancelChanges}
            />
        </ParentContainer>
    );
};

export default EntranceFeeForm;
